import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import Flag from 'react-country-flag';
import { useLanguageSelector } from '../hooks/useLanguageSelector';

interface LanguageSelectorProps {
  onBeforeOpen?: () => void;
}

export default function LanguageSelector({ onBeforeOpen }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const { languages, currentLang, currentCountryCode, changeLanguage } = useLanguageSelector();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!open) return;
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleOpen = () => {
    if (!open) onBeforeOpen?.();
    setOpen(!open);
  };

  return (
    <>
      {/* SVG gooey filter — only applied when open so it doesn't
          distort the semi-transparent closed button */}
      <svg className="absolute w-0 h-0" aria-hidden="true">
        <defs>
          <filter id="gooey">
            <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur" />
            <feColorMatrix in="blur" mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 30 -12"
              result="gooey" />
            {/* Dilate the gooey shape to create a border */}
            <feMorphology in="gooey" operator="dilate" radius="1" result="border" />
            {/* Color the border grey */}
            <feColorMatrix in="border" mode="matrix"
              values="0 0 0 0 0.78  0 0 0 0 0.78  0 0 0 0 0.78  0 0 0 1 0"
              result="borderColored" />
            {/* Composite: original on top of border */}
            <feComposite in="gooey" in2="borderColored" operator="over" result="withBorder" />
            <feComposite in="SourceGraphic" in2="withBorder" operator="atop" />
          </filter>
        </defs>
      </svg>
      {/* Gooey language selector — animate height (not opacity)
          so the filter sees fully opaque shapes on every frame */}
      <div
        ref={ref}
        className="relative"
        style={open ? { filter: "url(#gooey)" } : undefined}
      >
        <button
          aria-label={t('settings.language', 'Language')}
          className={`
            flex items-center gap-1.5 px-3 py-2 rounded-lg
            focus:outline-none focus:ring-0
            ${open
              ? 'bg-white text-zinc-800'
              : 'bg-white/20 text-white hover:bg-white/30'}
          `}
          style={open ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : undefined}
          onClick={handleOpen}
        >
          <Flag
            countryCode={currentCountryCode}
            svg
            style={{ width: '1.25rem', height: '1.25rem', borderRadius: '2px' }}
          />
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        </button>
        <div
          className={`absolute right-0 w-48 bg-white rounded-2xl rounded-tr-none overflow-hidden z-[120] ${open ? 'transition-[max-height] duration-300 ease-out' : ''}`}
          style={{
            top: 'calc(100% - 2px)',
            maxHeight: open ? `${languages.length * 52 + 8}px` : '0px',
            pointerEvents: open ? 'auto' : 'none',
          }}
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`
                w-full flex items-center gap-3 px-4 py-3
                text-sm font-medium transition-colors duration-150
                ${currentLang === lang.code
                  ? 'text-blue-600'
                  : 'text-zinc-800 hover:bg-zinc-50'}
              `}
              onClick={() => {
                changeLanguage(lang.code);
                setOpen(false);
              }}
            >
              <Flag
                countryCode={lang.countryCode}
                svg
                style={{ width: '1.25rem', height: '1.25rem', borderRadius: '2px' }}
              />
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
